import { useState, useEffect } from 'react';
import { message } from 'antd';
import { storage } from '../../utils/storage';
import { isAdmin as checkIsAdmin } from '../../utils/userUtils';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  phone?: string;
  permissions?: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface AdminAuthState {
  isAdmin: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
}

export const useAdminAuth = (): AdminAuthState => {
  const [state, setState] = useState<AdminAuthState>({
    isAdmin: false,
    user: null,
    loading: true,
    error: null,
  });

  const checkAdminAccess = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const token = storage.getAuthToken();
      const currentUser = storage.getUserData();

      if (!token || !currentUser) {
        setState({
          isAdmin: false,
          user: null,
          loading: false,
          error: 'No authenticated user found',
        });
        return;
      }

      const userIsAdmin = checkIsAdmin(currentUser);

      if (!userIsAdmin) {
        setState({
          isAdmin: false,
          user: currentUser,
          loading: false,
          error: 'Insufficient permissions. Admin access required.',
        });
        return;
      }

      setState({
        isAdmin: true,
        user: currentUser,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error('Admin auth check failed:', error);
      setState({
        isAdmin: false,
        user: null,
        loading: false,
        error: 'Failed to verify admin access',
      });
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      await checkAdminAccess();
    };
    initializeAuth();
  }, []);

  return state;
};
